import { useCallback, useMemo } from 'react'

import { useTranslation } from '../../../app/_providers/TranslationProvider.client'

export function useCtaButton({
  hasEmbroidery,
  hasSize,
  invalid,
  isValidToAdd,
  onAddToCart,
  onSelectEmbroidery,
  onViewProduct,
  soldOut,
  waitlistable,
  sizeSelectorClassName,
}: {
  hasEmbroidery: boolean
  hasSize: boolean
  invalid: boolean
  isValidToAdd: boolean
  onAddToCart(): void
  onSelectEmbroidery(): void
  onViewProduct(): void
  soldOut: boolean
  waitlistable: boolean
  sizeSelectorClassName?: string
}) {
  const {
    selectAndAddVariant: {
      addToBagButton: { cta, selectEmbroidery, viewProduct, soldOutCta },
    },
  } = useTranslation()

  const disabled = invalid && !hasSize
  const focusSizeSelector = useCallback((sizeSelectorClassName: string) => {
    setTimeout(() => {
      const sizeSections = document.getElementsByClassName(sizeSelectorClassName)
      const elementWithError = Array.from(sizeSections).find(
        el => el.getAttribute('data-has-error') === 'true'
      ) as HTMLElement | undefined
      if (elementWithError) {
        try {
          elementWithError.scrollIntoView({ behavior: 'smooth', block: 'start' })
          elementWithError.focus({ preventScroll: true })
        } catch {}
      }
    }, 0)
  }, [])
  return useMemo(() => {
    if (!soldOut && hasEmbroidery && isValidToAdd) {
      return {
        disabled: false,
        onClick: () => {
          onSelectEmbroidery()
        },
        text: selectEmbroidery,
        variant: 'filled' as const,
      }
    }

    if (!soldOut && hasEmbroidery && !isValidToAdd) {
      return {
        disabled: true,
        onClick: () => {
          if (sizeSelectorClassName) focusSizeSelector(sizeSelectorClassName)
        },
        text: selectEmbroidery,
        variant: 'filled' as const,
      }
    }

    if (disabled) {
      return {
        disabled,
        onClick: () => {
          if (sizeSelectorClassName) focusSizeSelector(sizeSelectorClassName)
        },
        text: cta,
        variant: 'filled' as const,
      }
    }

    if (!soldOut) {
      return {
        disabled,
        onClick: onAddToCart,
        text: cta,
        variant: 'filled' as const,
      }
    }

    if (!waitlistable) {
      return {
        disabled,
        onClick: onAddToCart,
        text: soldOutCta,
        variant: 'filled' as const,
      }
    }
    return {
      disabled,
      onClick: onViewProduct,
      text: viewProduct,
      variant: 'ghost' as const,
    }
  }, [
    disabled,
    hasEmbroidery,
    isValidToAdd,
    onAddToCart,
    onSelectEmbroidery,
    onViewProduct,
    selectEmbroidery,
    cta,
    soldOut,
    soldOutCta,
    viewProduct,
    waitlistable,
    sizeSelectorClassName,
    focusSizeSelector,
  ])
}
